import type { ComponentProps, ReactNode } from 'react'
import { type VariantProps } from 'tailwind-variants'

import { cn } from '@gto/ui/cn'
import { INVALID_MARKER } from '@gto/ui/state-markers'

import { inputVariants } from './input.styles'
import { InputAddon } from './input-addon'

type InputGroupVariantProps = VariantProps<typeof inputVariants>

export type InputGroupProps = ComponentProps<'div'> &
  InputGroupVariantProps & {
    startAddon?: ReactNode
    endAddon?: ReactNode
    /**
     * The control is invalid: the group takes the error border, and an enclosing `Field` or
     * `GroupedField` label turns the error colour. `Input` sets it from its `aria-invalid`.
     */
    invalid?: boolean
  }

export const InputGroup = ({
  className,
  size,
  variant,
  startAddon = null,
  endAddon = null,
  invalid = false,
  children,
  ...props
}: InputGroupProps) => {
  const { root } = inputVariants({ size, variant })

  return (
    <div
      className={cn(root(), invalid && INVALID_MARKER, className)}
      {...props}
    >
      {startAddon !== null && <InputAddon data-slot="start-addon">{startAddon}</InputAddon>}
      {children}
      {endAddon !== null && <InputAddon data-slot="end-addon">{endAddon}</InputAddon>}
    </div>
  )
}
